/* eslint-disable @typescript-eslint/naming-convention */
import type { ValueOf } from 'type-fest';

import type { Locale } from '@change-corgi/config/locales';

import { intlNumberFormatLocaleMapping } from '#config/intl-numberformat';

import { loadMissingFeatures } from '../polyfills';
import type { IntlCheck } from '../types';

const importMapping: Record<ValueOf<typeof intlNumberFormatLocaleMapping>, () => Promise<unknown>> = {
	en: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/en" */ '@formatjs/intl-numberformat/locale-data/en.js'),
	'en-AU': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-numberformat/en-AU" */ '@formatjs/intl-numberformat/locale-data/en-AU.js'
		),
	'en-GB': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-numberformat/en-GB" */ '@formatjs/intl-numberformat/locale-data/en-GB.js'
		),
	'en-IN': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-numberformat/en-IN" */ '@formatjs/intl-numberformat/locale-data/en-IN.js'
		),
	'en-CA': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-numberformat/en-CA" */ '@formatjs/intl-numberformat/locale-data/en-CA.js'
		),
	de: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/de" */ '@formatjs/intl-numberformat/locale-data/de.js'),
	fr: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/fr" */ '@formatjs/intl-numberformat/locale-data/fr.js'),
	hi: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/hi" */ '@formatjs/intl-numberformat/locale-data/hi.js'),
	it: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/it" */ '@formatjs/intl-numberformat/locale-data/it.js'),
	ja: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/ja" */ '@formatjs/intl-numberformat/locale-data/ja.js'),
	ko: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/ko" */ '@formatjs/intl-numberformat/locale-data/ko.js'),
	pt: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/pt" */ '@formatjs/intl-numberformat/locale-data/pt.js'),
	ru: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/ru" */ '@formatjs/intl-numberformat/locale-data/ru.js'),
	es: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/es" */ '@formatjs/intl-numberformat/locale-data/es.js'),
	'es-419': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-numberformat/es-419" */ '@formatjs/intl-numberformat/locale-data/es-419.js'
		),
	'es-AR': async () =>
		import(
			/* webpackChunkName: "polyfill/intl-numberformat/es-AR" */ '@formatjs/intl-numberformat/locale-data/es-AR.js'
		),
	id: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/id" */ '@formatjs/intl-numberformat/locale-data/id.js'),
	tr: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/tr" */ '@formatjs/intl-numberformat/locale-data/tr.js'),
	th: async () =>
		import(/* webpackChunkName: "polyfill/intl-numberformat/th" */ '@formatjs/intl-numberformat/locale-data/th.js'),
};

export async function loadPolyfill(locale: string, deps: readonly IntlCheck[]): Promise<void> {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const intlLocale = intlNumberFormatLocaleMapping[locale as Locale];
	await loadMissingFeatures(deps, locale);
	await import(/* webpackChunkName: "polyfill/intl-numberformat/polyfill" */ '@formatjs/intl-numberformat/polyfill.js');
	await importMapping[intlLocale]();
}
